import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useStudioTheme } from "../constants";
import RegoditLogo from "../../../Assets/Images/Logo.png";

const LeftRail = ({
  fileTitle,
  docType,
  conversations = [],
  activeConversationId,
  onNewConversation,
  onSelectConversation,
  onBack,
}) => {
  const { palette, fontStack } = useStudioTheme();

  return (
    <Box
      sx={{
        width: 248,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: palette.paper,
        borderRight: `1px solid ${palette.border}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 2.25 }}>
        <Box
          component="img"
          src={RegoditLogo}
          alt="Regodit"
          sx={{ width: 28, height: 28, objectFit: "contain" }}
        />
        <Box>
          <Typography
            sx={{
              fontFamily: fontStack,
              fontSize: "14px",
              fontWeight: 700,
              color: palette.ink,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            Rego AI
          </Typography>
          <Typography sx={{ fontFamily: fontStack, fontSize: "11px", color: palette.inkFaint }}>
            Policy editor
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 1.75, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.5,
            py: 1.25,
            borderRadius: "10px",
            backgroundColor: palette.brandTint,
            borderLeft: `3px solid ${palette.brand}`,
          }}
        >
          <DescriptionRoundedIcon sx={{ fontSize: 18, color: palette.brand, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: fontStack,
                fontSize: "13px",
                fontWeight: 600,
                color: palette.ink,
              }}
            >
              {fileTitle || "Untitled document"}
            </Typography>
            <Typography
              sx={{
                fontFamily: fontStack,
                fontSize: "11px",
                color: palette.inkSoft,
                textTransform: "capitalize",
              }}
            >
              {docType || "document"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 0.75,
          pb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: fontStack,
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: palette.inkFaint,
          }}
        >
          Session
        </Typography>
        <Tooltip title="New conversation">
          <Box
            onClick={onNewConversation}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: palette.inkSoft,
              transition: "all 0.15s ease",
              "&:hover": { backgroundColor: palette.infoHover, color: palette.brand },
            }}
          >
            <AddRoundedIcon sx={{ fontSize: 17 }} />
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 1 }}>
        {conversations.map((c) => {
          const isActive = c.id === activeConversationId;
          const preview =
            c.messages?.find((m) => m.role === "user")?.content || "New conversation";
          return (
            <Box
              key={c.id}
              onClick={() => onSelectConversation(c.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.9,
                mb: 0.25,
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: isActive ? palette.infoHover : "transparent",
                borderLeft: isActive ? `2px solid ${palette.brand}` : "2px solid transparent",
                transition: "background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: isActive ? palette.infoHover : palette.hoverBg,
                },
              }}
            >
              <ChatBubbleOutlineRoundedIcon
                sx={{
                  fontSize: 15,
                  flexShrink: 0,
                  color: isActive ? palette.brand : palette.inkFaint,
                }}
              />
              <Typography
                noWrap
                sx={{
                  fontFamily: fontStack,
                  fontSize: "12.5px",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? palette.ink : palette.inkSoft,
                }}
              >
                {c.title || preview}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ p: 1.5, borderTop: `1px solid ${palette.border}` }}>
        <Box
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.25,
            py: 0.85,
            borderRadius: "8px",
            cursor: "pointer",
            color: palette.inkSoft,
            fontFamily: fontStack,
            fontSize: "12.5px",
            fontWeight: 600,
            transition: "all 0.15s ease",
            "&:hover": { backgroundColor: palette.hoverBg, color: palette.ink },
          }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
          Back to policy
        </Box>
      </Box>
    </Box>
  );
};

export default LeftRail;
